import type { AddOnProductUnitName } from "schemas/generated/addOns";
import type {
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";

import { getPublisherUrl } from "@v2/utils/env";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { pluralize } from "replo-utils/lib/pluralize";
import {
  ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH,
  ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME,
  ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH,
  ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME,
  ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH,
  ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME,
} from "schemas/addOns";

export const getStripePortalUrl = (workspaceId: string) => {
  const url = new URL(`${getPublisherUrl()}/api/v1/stripe/portal`);
  url.searchParams.append("workspaceId", workspaceId);
  url.searchParams.append("returnUrl", window.location.href);
  return url.toString();
};

export const getPlanRank = (tier: BillingTier) => {
  const planRanks = {
    free: 0,
    starter: 1,
    pro: 2,
    custom: 3,
  };
  return planRanks[tier as keyof typeof planRanks] ?? 0;
};

export const getCountOfAdditionalSitesInSubscription = (
  subscription: SubscriptionDetails,
) => {
  return subscription.productUnits.filter(
    (unit) => unit.name === ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME,
  ).length;
};

export const getCountOfAdditionalProjectsInSubscription = (
  subscription: SubscriptionDetails,
) => {
  return subscription.productUnits.filter(
    (unit) => unit.name === ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME,
  ).length;
};

export const getCountOfAdditionalPublishedElementBundlesInSubscription = (
  subscription: SubscriptionDetails,
) => {
  return subscription.productUnits.filter((unit) => {
    return unit.name === ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME;
  }).length;
};

export const getAddOnDisplayInfo = ({
  type,
  quantity,
}: {
  type: AddOnProductUnitName;
  quantity: number;
}): { label: string; pricePerMonth: number } => {
  return exhaustiveSwitch({ type })({
    [ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME]: () => ({
      label: `Shopify Theme ${pluralize({
        singular: "Project",
        plural: "Projects",
        count: quantity,
      })}`,
      pricePerMonth: ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH,
    }),
    [ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME]: () => ({
      label: `Replo ${pluralize({
        singular: "Site",
        plural: "Sites",
        count: quantity,
      })}`,
      pricePerMonth: ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH,
    }),
    [ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME]: () => ({
      label: `Published Element ${pluralize({
        singular: "Bundle",
        plural: "Bundles",
        count: quantity,
      })}`,
      pricePerMonth: ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH,
    }),
  });
};
